// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: what changed in a shared pad. An info icon beside "saved" opens a card on hover
// (tap on phones) listing the latest editing sessions: who, when, and the lines they
// added, ticked, edited or removed. The icon carries a dot once when someone else has
// changed the pad since this device last looked. The chat's "updated the shared pad"
// notice shows the same card for its own session.

import styles from '@app/features/konvo_scratchpad/components/ScratchpadDrawer.module.css';
import {AvatarStack} from '@app/features/konvo_scratchpad/components/ScratchpadHome';
import {PopMenu, type PopMenuAnchor} from '@app/features/konvo_scratchpad/components/ScratchpadPopMenu';
import type {ChannelPad, PadChange, PadChangeItem} from '@app/features/konvo_scratchpad/state/KonvoChannelScratchpad';
import {currentUserId, personById} from '@app/features/konvo_scratchpad/state/ScratchpadBridge';
import {
	CheckCircleIcon,
	CircleIcon,
	InfoIcon,
	MinusIcon,
	PencilSimpleIcon,
	PlusIcon,
} from '@app/features/ui/icons/Icons';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';

const CARD_WIDTH = 304;
const CLOSE_DELAY_MS = 180;

function sameDay(a: Date, b: Date): boolean {
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** "09:53" today, "Yesterday 16:10", otherwise "Mon 6 Oct, 16:10". */
function useWhen(): (iso: string) => string {
	const {t} = useLingui();
	return (iso) => {
		const date = new Date(iso);
		const time = date.toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit'});
		const now = new Date();
		if (sameDay(date, now)) return time;
		const yesterday = new Date(now);
		yesterday.setDate(now.getDate() - 1);
		if (sameDay(date, yesterday)) return t`Yesterday ${time}`;
		return date.toLocaleString(undefined, {
			weekday: 'short',
			day: 'numeric',
			month: 'short',
			hour: '2-digit',
			minute: '2-digit',
		});
	};
}

function ItemIcon({kind}: {kind: PadChangeItem['kind']}) {
	if (kind === 'ticked') return <CheckCircleIcon size={15} weight="fill" />;
	if (kind === 'unticked') return <CircleIcon size={15} />;
	if (kind === 'edited') return <PencilSimpleIcon size={14} />;
	if (kind === 'removed') return <MinusIcon size={14} />;
	return <PlusIcon size={14} />;
}

function ChangeItem({item}: {item: PadChangeItem}) {
	const {t} = useLingui();
	const label =
		item.kind === 'added'
			? t`Added`
			: item.kind === 'removed'
				? t`Removed`
				: item.kind === 'ticked'
					? t`Ticked`
					: item.kind === 'unticked'
						? t`Unticked`
						: t`Edited`;
	return (
		<li
			className={clsx(
				styles.changeItem,
				item.kind === 'ticked' && styles.changeTicked,
				item.kind === 'removed' && styles.changeRemoved,
			)}
			data-kind={item.kind}
		>
			<span className={styles.changeIcon} role="img" title={label} aria-label={label}>
				<ItemIcon kind={item.kind} />
			</span>
			<span className={styles.changeText}>
				{item.kind === 'edited' && item.from !== item.text && <s className={styles.changeFrom}>{item.from}</s>}
				<span>{item.text}</span>
			</span>
		</li>
	);
}

function ChangeSession({change}: {change: PadChange}) {
	const {t} = useLingui();
	const when = useWhen();
	const me = currentUserId();
	const person = personById(change.user_id);
	const name = change.user_id === me ? t`You` : (person?.name.split(' ')[0] ?? t`Someone`);
	return (
		<section className={styles.changeSession} data-flx="konvo-scratchpad.change-session">
			<header className={styles.changeHead}>
				<AvatarStack people={[person ?? {id: change.user_id, name, avatar: null}]} />
				<b>{name}</b>
				<span>{when(change.updated_at)}</span>
			</header>
			<ul className={styles.changeItems}>
				{change.items.map((item, index) => (
					<ChangeItem key={`${item.kind}-${index}`} item={item} />
				))}
			</ul>
			{change.more > 0 && <span className={styles.changeMore}>{t`and ${change.more} more`}</span>}
		</section>
	);
}

/** The card body: the sessions, or why there is nothing to show yet. */
const ChangesBody = observer(function ChangesBody({
	pad,
	only,
}: {
	pad: ChannelPad;
	only?: (change: PadChange) => boolean;
}) {
	const {t} = useLingui();
	const changes = pad.changes;
	if (!changes) {
		return (
			<p className={styles.changesEmpty}>
				{pad.changesStatus === 'failed' ? t`Couldn't load the changes. Try again.` : t`Loading…`}
			</p>
		);
	}
	const shown = only ? changes.filter(only).slice(0, 1) : changes;
	if (shown.length === 0) {
		return (
			<p className={styles.changesEmpty}>{only ? t`No note changes to show for this one.` : t`No changes yet.`}</p>
		);
	}
	return (
		<div className={styles.changesList}>
			{shown.map((change) => (
				<ChangeSession key={`${change.user_id}-${change.started_at}`} change={change} />
			))}
		</div>
	);
});

/**
 * Open on hover with a little grace to move onto the card. Touch has no hover: a tap
 * toggles it, and the pointer events a tap also sends are ignored.
 */
function useHoverCard(onOpen: () => void, placement: 'below' | 'above') {
	const [anchor, setAnchor] = useState<PopMenuAnchor | null>(null);
	const closeTimer = useRef<number | null>(null);
	const closedAt = useRef(0);

	const cancelClose = useCallback(() => {
		if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
		closeTimer.current = null;
	}, []);
	useEffect(() => cancelClose, [cancelClose]);

	const show = useCallback(
		(element: HTMLElement) => {
			cancelClose();
			const rect = element.getBoundingClientRect();
			setAnchor({x: rect.left - 12, y: placement === 'above' ? rect.top - 6 : rect.bottom + 6});
			onOpen();
		},
		[cancelClose, onOpen, placement],
	);
	const close = useCallback(() => {
		cancelClose();
		closedAt.current = Date.now();
		setAnchor(null);
	}, [cancelClose]);
	const closeSoon = useCallback(() => {
		cancelClose();
		closeTimer.current = window.setTimeout(close, CLOSE_DELAY_MS);
	}, [cancelClose, close]);

	const trigger = {
		onPointerEnter: (event: React.PointerEvent<HTMLElement>) => {
			if (event.pointerType === 'mouse') show(event.currentTarget);
		},
		onPointerLeave: (event: React.PointerEvent<HTMLElement>) => {
			if (event.pointerType === 'mouse') closeSoon();
		},
		onClick: (event: React.MouseEvent<HTMLElement>) => {
			// A press on the trigger while open closes the card (outside-press handling) before it gets here.
			if (Date.now() - closedAt.current < 300) return;
			if (anchor) close();
			else show(event.currentTarget);
		},
	};
	const card = {
		onPointerEnter: cancelClose,
		onPointerLeave: (event: React.PointerEvent<HTMLElement>) => {
			if (event.pointerType === 'mouse') closeSoon();
		},
	};
	return {anchor, trigger, card, close};
}

/** The info icon beside "saved" in a shared pad, with the change card. */
export const SharedPadChangesButton = observer(function SharedPadChangesButton({pad}: {pad: ChannelPad}) {
	const {t} = useLingui();
	const onOpen = useCallback(() => {
		void pad.loadChanges();
		pad.markChangesSeen();
	}, [pad]);
	const {anchor, trigger, card, close} = useHoverCard(onOpen, 'below');
	const unseen = pad.hasUnseenChanges;
	return (
		<>
			<button
				type="button"
				className={clsx(styles.changesButton, anchor && styles.changesButtonOpen)}
				aria-label={unseen ? t`See what changed (new changes)` : t`See what changed`}
				aria-expanded={anchor != null}
				data-flx="konvo-scratchpad.changes"
				{...trigger}
			>
				<InfoIcon size={15} weight="regular" />
				{unseen && <span className={styles.changesDot} data-flx="konvo-scratchpad.changes-dot" />}
			</button>
			{anchor && (
				<PopMenu anchor={anchor} items={[]} onClose={close} width={CARD_WIDTH}>
					<div className={styles.changesCard} data-flx="konvo-scratchpad.changes-card" {...card}>
						<b className={styles.changesTitle}>{t`Recent changes`}</b>
						<ChangesBody pad={pad} />
					</div>
				</PopMenu>
			)}
		</>
	);
});

/** The session a chat notice announced: the same person, starting around the notice. */
export function sessionForNotice(userId: string, noticeAt: Date): (change: PadChange) => boolean {
	const at = noticeAt.getTime();
	return (change) => {
		if (change.user_id !== userId) return false;
		const started = Date.parse(change.started_at);
		const ended = Date.parse(change.updated_at);
		// The notice goes out on the first save; the first save that changes a line can come a little later.
		return started <= at + 5 * 60_000 && ended >= at - 60_000;
	};
}

/** A trigger (the notice's "shared pad" link) that shows the session the notice is about. */
export const PadNoticeChanges = observer(function PadNoticeChanges({
	pad,
	userId,
	noticeAt,
	children,
	className,
	onClick,
	flx,
}: {
	pad: ChannelPad;
	userId: string;
	noticeAt: Date;
	children: React.ReactNode;
	className?: string;
	onClick: () => void;
	flx?: string;
}) {
	const {t} = useLingui();
	const onOpen = useCallback(() => void pad.loadChanges(), [pad]);
	const {anchor, trigger, card, close} = useHoverCard(onOpen, 'above');
	const only = useCallback((change: PadChange) => sessionForNotice(userId, noticeAt)(change), [userId, noticeAt]);
	return (
		<>
			<button
				type="button"
				className={className}
				data-flx={flx}
				onPointerEnter={trigger.onPointerEnter}
				onPointerLeave={trigger.onPointerLeave}
				onClick={() => {
					close();
					onClick();
				}}
			>
				{children}
			</button>
			{anchor && (
				<PopMenu anchor={anchor} items={[]} onClose={close} width={CARD_WIDTH} placement="above">
					<div className={styles.changesCard} data-flx="konvo-scratchpad.notice-changes-card" {...card}>
						<b className={styles.changesTitle}>{t`What changed`}</b>
						<ChangesBody pad={pad} only={only} />
					</div>
				</PopMenu>
			)}
		</>
	);
});
